import React from 'react';
import { Stack, CircularProgress, Typography } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { SalesSummary, PipelineStage, MonthlyAmount, QuotationOutcome, TopDeal } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import HBars from '../charts/HBars';
import Columns from '../charts/Columns';
import TableView from '../charts/TableView';
import { fmtMoney, CURRENCY_SYMBOL } from './format';

const BUSINESS_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant];

const loadSales = (institutionId: string): Promise<SalesSummary> =>
    DashboardService.getSalesSummary(institutionId);

const SalesWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadSales, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.mtdSalesTotal === 0 && d.pipelineCount === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No sales activity this month — issue invoices or send quotations to see figures here.</Typography>;
    }
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {/* Revenue earned is genuinely good news; pipeline is only potential, so it stays
                neutral however large it is. */}
            <StatTile label="MTD sales" unit={CURRENCY_SYMBOL} value={fmtMoney(d.mtdSalesTotal)} tone={d.mtdSalesTotal > 0 ? 'good' : 'default'} />
            <StatTile label="Pipeline" unit={CURRENCY_SYMBOL} value={fmtMoney(d.pipelineValue)} sub={`${d.pipelineCount} open`} />
        </Stack>
    );
};

const loadPipeline = (institutionId: string): Promise<PipelineStage[]> =>
    DashboardService.getSalesPipeline(institutionId);

const SalesPipelineWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadPipeline, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The backend zero-fills the fixed DRAFT/SENT/CONFIRMED set, so a zero-amount stage is a
    // real "nothing at this stage" figure and stays on the chart as a zero bar.
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No quotations yet — create quotations to build a pipeline.</Typography>;
    }
    return (
        <HBars
            data={d.map(s => ({
                label: s.status,
                value: s.amount,
                tooltip: [
                    { label: 'Value', value: fmtMoney(s.amount) },
                    { label: 'Quotations', value: String(s.count) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadSalesRevenue = (institutionId: string): Promise<MonthlyAmount[]> =>
    DashboardService.getSalesRevenue(institutionId);

const SalesRevenueWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadSalesRevenue, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No invoiced revenue yet — issue invoices to see monthly revenue.</Typography>;
    }
    return <Columns data={d.map(m => ({ label: m.month, value: m.amount }))} yFmt={fmtMoney} />;
};

const loadOutcomes = (institutionId: string): Promise<QuotationOutcome[]> =>
    DashboardService.getSalesOutcomes(institutionId);

const SalesOutcomesWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadOutcomes, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No quotation outcomes in the last 90 days.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <HBars
                data={d.map(o => ({
                    label: o.status,
                    value: o.count,
                    tooltip: [
                        { label: 'Quotations', value: String(o.count) },
                        { label: 'Value', value: fmtMoney(o.amount) },
                    ],
                }))}
            />
            <Typography variant="caption" color="text.secondary">Last 90 days</Typography>
        </Stack>
    );
};

const loadTopDeals = (institutionId: string): Promise<TopDeal[]> =>
    DashboardService.getSalesTopDeals(institutionId);

const SalesTopDealsWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadTopDeals, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No open deals — send quotations to see top deals here.</Typography>;
    }
    return (
        <TableView
            columns={['Quotation', 'Customer', 'Stage', 'Value']}
            rows={d.map(q => [q.quotationNumber, q.customerName, q.status, fmtMoney(q.amount)])}
        />
    );
};

export const salesWidgets: WidgetDef[] = [
    { id: 'sales.summary', module: 'sales', title: 'Sales — MTD & pipeline', roles: BUSINESS_ROLES, size: 'md', component: SalesWidget },
    { id: 'sales.pipeline', module: 'sales', title: 'Sales — pipeline by stage', roles: BUSINESS_ROLES, size: 'md', component: SalesPipelineWidget },
    { id: 'sales.revenue', module: 'sales', title: 'Sales — invoiced revenue', roles: BUSINESS_ROLES, size: 'md', component: SalesRevenueWidget },
    { id: 'sales.outcomes', module: 'sales', title: 'Sales — quotation outcomes', roles: BUSINESS_ROLES, size: 'md', component: SalesOutcomesWidget },
    { id: 'sales.topDeals', module: 'sales', title: 'Sales — top open deals', roles: BUSINESS_ROLES, size: 'lg', component: SalesTopDealsWidget },
];
